import {PrimeIcons} from "primereact/api";
import {Button} from "primereact/button";
import {Chip} from "primereact/chip";
import {Toast} from "primereact/toast";
import {useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";

import AppreciationInputText from "../component/appreciation/AppreciationInputText";
import AppreciationToolbar from "../component/appreciation/AppreciationToolbar";
import {useAppreciationVisible} from "../component/appreciation/appreciationHooks";
import {DownloadMapPdfButton} from "../component/DownloadMapPdfButton";
import I18nErrorMessage from "../component/I18nErrorMessage";
import {MainFrame, MainToolbar} from "../component/MainFrame";
import {selectAddressData, selectError} from "../redux/addressSlice";
import {selectAppreciationValueOrDefaultCurried} from "../redux/appreciationsUtil";
import {LocationPdfType} from "../redux/pdfConstants";
import {selectGeorefRefreshLocked, selectPersistenceQueryError, selectReadOnly} from "../redux/persistenceSlice";
import {uncacheAddressAsync} from "../redux/refreshGeorefThunk";
import {resetAll} from "../redux/resetThunk";
import {useEffectRequiresValidRevaId} from "../util/hooks";
import AddressDetails from "./address/AddressDetails";
import AddressMap from "./address/AddressMap";

const APPRECIATION_KEY = {page: "location", tab: "overview"};

/**
 * @returns {React.JSX.Element}
 */
function PageAddress() {
    const {t} = useTranslation();
    const dispatch = useDispatch();

    const addressData = useSelector(selectAddressData);
    const queryError = useSelector(selectError);
    const persistenceQueryError = useSelector(selectPersistenceQueryError);

    const addressSelected = !!addressData;

    const toast = useRef(null);
    const [appreciationVisible, setAppreciationVisible] = useState(false);
    // Bei jedem Klick auf die Adresskachel hochzählen -> Übersichtskarte wird auf
    // den Default zurückgesetzt (Haus zentriert, 1:250k).
    const [mapResetCounter, setMapResetCounter] = useState(0);
    const readOnly = useSelector(selectReadOnly);
    const georefRefreshLocked = useSelector(selectGeorefRefreshLocked);
    const appreciationValue = useSelector(selectAppreciationValueOrDefaultCurried(APPRECIATION_KEY));
    const appreciationAllowed = useAppreciationVisible(APPRECIATION_KEY);
    const showAppreciation =
        appreciationAllowed && (appreciationVisible || readOnly) && !(readOnly && !appreciationValue);

    useEffectRequiresValidRevaId();

    const handleRemoveAddress = () => {
        dispatch(resetAll());
    };

    // Externe Georeferenz aus dem Cache entfernen (Icon oben-rechts in der Adresskachel).
    const handleUncacheGeoref = () => {
        dispatch(uncacheAddressAsync(addressData)).then((success) => {
            if (success === false) {
                toast.current.show({
                    severity: "warn",
                    detail: t("address.uncache_external_georef_skipped"),
                    life: 2000,
                });
                return;
            }
            if (success === null) {
                toast.current.show({
                    severity: "error",
                    detail: t("address.uncache_external_georef_failed"),
                    life: 2000,
                });
                return;
            }
            toast.current.show({
                severity: "success",
                detail: t("address.uncache_external_georef_reloading"),
                life: 2000,
            });
            setTimeout(() => window.location.reload(), 2000);
        });
    };

    return (
        <MainFrame>
            <Toast ref={toast} />
            <MainToolbar title={t("address.address")}>
                {addressSelected && (
                    <Chip
                        className="text-lg"
                        label={addressData.orderAddressClean}
                        removable
                        onRemove={handleRemoveAddress}
                    />
                )}
                <div className="flex flex-1 justify-end">
                    <AppreciationToolbar
                        page="location"
                        tab="overview"
                        appreciationVisible={appreciationVisible}
                        setAppreciationVisible={setAppreciationVisible}
                    />
                    <DownloadMapPdfButton tooltip={t("address.download_pdf_tooltip")} type={LocationPdfType.OVERVIEW} />
                    <DownloadMapPdfButton
                        tooltip={t("address.download_location_pdf_tooltip")}
                        type={LocationPdfType.LOCATION}
                        icon={PrimeIcons.IMAGES}
                    />
                </div>
            </MainToolbar>
            <I18nErrorMessage className="mb-4" errorObject={persistenceQueryError} />
            <I18nErrorMessage errorObject={queryError} />

            {addressSelected && showAppreciation && (
                <div className="p-inputtext-sm text-sm mt-0 mb-4">
                    <AppreciationInputText page="location" tab="overview" />
                </div>
            )}

            {addressSelected && (
                <div className="grow shrink-0 MapSplit">
                    <div className="MapSplitLeft flex flex-col">
                        {/* Relativer Wrapper NUR um die Adresskachel, damit der Georef-Button in
                            deren freier unterer rechter Ecke sitzt (nicht am Ende der ganzen Spalte). */}
                        <div className="relative">
                            {/* Georef-neu-erzwingen-Button: unten-rechts in der Adresskachel, gefüllt
                                (gut sichtbar), Reset-/Wiederholen-Icon; ausgeblendet, wenn
                                selectGeorefRefreshLocked greift. */}
                            {!georefRefreshLocked && (
                                <Button
                                    id="uncacheGeorefButton"
                                    icon="pi pi-replay"
                                    rounded
                                    severity="secondary"
                                    className="absolute bottom-2 right-2 z-10"
                                    style={{fontSize: "1.15rem"}}
                                    tooltip={t("address.uncache_external_georef")}
                                    tooltipOptions={{position: "left"}}
                                    onClick={handleUncacheGeoref}
                                />
                            )}
                            <AddressDetails
                                addressData={addressData}
                                onSelectAddress={() => setMapResetCounter((c) => c + 1)}
                            />
                        </div>
                    </div>
                    <div className="MapSplitRight OpenLayersMapBackdrop">
                        <AddressMap address={addressData.address} resetSignal={mapResetCounter} />
                    </div>
                </div>
            )}
        </MainFrame>
    );
}

export default PageAddress;
